import React, { useState, useMemo } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';
import { formatCurrency } from '../../utils/formatters';

const PLAN_TYPES = {
  CREDIT: 'credit',
  PIECES: 'pieces',
};

const SubscriptionPlanForm = ({ plan, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    planType: plan?.planType || PLAN_TYPES.CREDIT,
    price: plan?.price ?? '',
    creditAmount: plan?.creditAmount ?? '',
    pieceLimit: plan?.pieceLimit ?? '',
    hasExpiry: plan?.hasExpiry !== undefined ? plan.hasExpiry : true,
    durationDays: plan?.durationDays ?? 180,
    isActive: plan?.isActive !== undefined ? plan.isActive : true,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTypeChange = (planType) => {
    setFormData(prev => ({ ...prev, planType }));
    setErrors(prev => ({ ...prev, planType: '', creditAmount: '', pieceLimit: '' }));
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Plan name is required';
    if (formData.price === '' || Number(formData.price) < 0) next.price = 'A valid price is required';

    if (formData.planType === PLAN_TYPES.CREDIT) {
      const credit = Number(formData.creditAmount);
      if (!credit || credit <= 0) next.creditAmount = 'Credit amount must be greater than zero';
      else if (credit < Number(formData.price)) next.creditAmount = 'Credit must be at least the price paid';
    } else if (formData.planType === PLAN_TYPES.PIECES) {
      const pieces = Number(formData.pieceLimit);
      if (!pieces || pieces <= 0) next.pieceLimit = 'Piece limit must be greater than zero';
    }

    if (formData.hasExpiry) {
      const days = Number(formData.durationDays);
      if (!days || days < 1) next.durationDays = 'Duration must be at least 1 day';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim(),
      planType: formData.planType,
      price: parseFloat(formData.price),
      creditAmount: formData.planType === PLAN_TYPES.CREDIT ? parseFloat(formData.creditAmount) : 0,
      pieceLimit: formData.planType === PLAN_TYPES.PIECES ? parseInt(formData.pieceLimit, 10) : 0,
      hasExpiry: formData.hasExpiry,
      durationDays: formData.hasExpiry ? parseInt(formData.durationDays, 10) : 0,
      isActive: formData.isActive,
    });
  };

  const bonusValue = useMemo(() => {
    const credit = Number(formData.creditAmount) || 0;
    const price = Number(formData.price) || 0;
    return Math.max(0, credit - price);
  }, [formData.creditAmount, formData.price]);

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-row">
        <Input
          label="Plan Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="Plan A, Plan B, Family Saver…"
          required
        />
        <div className="input-group">
          <label className="input-label">Status</label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
            />
            <span>Active (available for new purchases)</span>
          </label>
        </div>
      </div>

      <div className="form-row">
        <div className="input-group">
          <label className="input-label">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input"
            rows="2"
            placeholder="Short summary shown to the cashier when selling this plan"
          />
        </div>
      </div>

      {/* Plan type selector */}
      <div className="form-section">
        <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
          Plan Type
        </h4>
        <div className="form-row" style={{ gap: 12 }}>
          <button
            type="button"
            className={`ps-method-btn ${formData.planType === PLAN_TYPES.CREDIT ? 'active' : ''}`}
            onClick={() => handleTypeChange(PLAN_TYPES.CREDIT)}
            style={{ flex: 1 }}
          >
            Credit Plan — pay X, get Y credit
          </button>
          <button
            type="button"
            className={`ps-method-btn ${formData.planType === PLAN_TYPES.PIECES ? 'active' : ''}`}
            onClick={() => handleTypeChange(PLAN_TYPES.PIECES)}
            style={{ flex: 1 }}
          >
            Pieces Plan — pay X, get N pieces
          </button>
        </div>
      </div>

      {/* Pricing */}
      <div className="form-row">
        <Input
          label="Price Paid (KWD)"
          name="price"
          type="number"
          step="0.001"
          min="0"
          value={formData.price}
          onChange={handleChange}
          error={errors.price}
          placeholder="20.000"
          required
        />

        {formData.planType === PLAN_TYPES.CREDIT ? (
          <Input
            label="Usable Credit (KWD)"
            name="creditAmount"
            type="number"
            step="0.001"
            min="0"
            value={formData.creditAmount}
            onChange={handleChange}
            error={errors.creditAmount}
            placeholder="30.000"
            required
          />
        ) : (
          <Input
            label="Pieces Included"
            name="pieceLimit"
            type="number"
            step="1"
            min="1"
            value={formData.pieceLimit}
            onChange={handleChange}
            error={errors.pieceLimit}
            placeholder="50"
            required
          />
        )}
      </div>

      {/* Expiry */}
      <div className="form-section">
        <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
          Expiry
        </h4>
        <div className="form-row">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasExpiry"
                checked={formData.hasExpiry}
                onChange={handleChange}
              />
              <span>Has expiry date</span>
            </label>
          </div>

          {formData.hasExpiry && (
            <Input
              label="Duration (days)"
              name="durationDays"
              type="number"
              min="1"
              value={formData.durationDays}
              onChange={handleChange}
              error={errors.durationDays}
              placeholder="180"
            />
          )}
        </div>
        {!formData.hasExpiry && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: -4 }}>
            Open-ended: the subscription remains valid until the customer uses up their balance.
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="pricing-examples" style={{
        background: '#f0f9ff',
        padding: '12px 16px',
        borderRadius: '8px',
        marginTop: '16px',
        border: '1px solid #bfdbfe',
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 8 }}>
          Customer-facing summary
        </div>
        <div style={{ fontSize: 12, color: '#1e3a8a', lineHeight: 1.6 }}>
          {formData.planType === PLAN_TYPES.CREDIT ? (
            <>
              Pay <strong>{formatCurrency(Number(formData.price) || 0)}</strong>, receive{' '}
              <strong>{formatCurrency(Number(formData.creditAmount) || 0)}</strong> in usable credit
              {bonusValue > 0 && <> — bonus value of <strong>{formatCurrency(bonusValue)}</strong></>}.
            </>
          ) : (
            <>
              Pay <strong>{formatCurrency(Number(formData.price) || 0)}</strong>, get{' '}
              <strong>{formData.pieceLimit || 0}</strong> pieces of laundry covered.
            </>
          )}
          {' '}
          {formData.hasExpiry
            ? <>Valid for <strong>{formData.durationDays || 0} day{Number(formData.durationDays) === 1 ? '' : 's'}</strong>.</>
            : <>No expiry.</>}
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {plan ? 'Update Plan' : 'Create Plan'}
        </Button>
      </div>
    </form>
  );
};

export default SubscriptionPlanForm;
